import React, { useState } from 'react';
import { Table, Tooltip, Checkbox, Modal } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import api from '../functions/api';
import getUserId from '../functions/getUserId';
import CompactAlertCard from './CompactAlertCard'; // DAS-2959: touched-alert drill-down

const Market_Alerts = () => {
  const dispatch = useDispatch();

  const marketAlerts = useSelector(state => {
    return state?.portfolio?.data?.market_alerts || [];
  });

  // DAS-2959: pools to resolve a touched alert id into its full record for the card
  const pfAlerts = useSelector(state => state?.portfolio?.data?.pf_alert);
  const opportunitiesAlerts = useSelector(state => state?.portfolio?.data?.opportunities_alerts);
  const favoritesAlerts = useSelector(state => state?.favorites?.data?.favorites_alert_data);
  const alertEffectiveness = useSelector(state => state?.portfolio?.data?.alert_effectiveness_data);
  const [alertModal, setAlertModal] = useState(null);

  const userId = getUserId();

  const formatAlertText = (text) => {
    if (!text) return '';
    const becauseIndex = text.indexOf('because:');
    if (becauseIndex === -1) return text;
    const beforeBecause = text.substring(0, becauseIndex + 8);
    const afterBecause = text.substring(becauseIndex + 8).replace(/ - /g, '\n- ');
    return `${beforeBecause}${afterBecause}`;
  };

  const isCheckedByUser = (seenby) => {
    if (!seenby) return false;
    const seenByUsers = seenby.split(',').map(id => id.trim());
    return seenByUsers.includes(userId.toString());
  };

  // DAS-2959: days between initiation and the (nightly) update — i.e. how long
  // this market signal has been standing.
  const daysActive = (record) => {
    if (!record?.epoch_initiation) return null;
    const days = Math.floor((Date.now() / 1000 - record.epoch_initiation) / 86400);
    return days >= 0 ? days : null;
  };

  // DAS-2959 (pattern from DAS-2897): open the touched alert's card IN PLACE.
  const openAlertCard = (id) => {
    const pools = [pfAlerts, opportunitiesAlerts, favoritesAlerts];
    for (const pool of pools) {
      const hit = Array.isArray(pool) ? pool.find(a => Number(a.ID) === Number(id)) : null;
      if (hit) { setAlertModal(hit); return; }
    }
  };

  const handleCheckboxChange = async (id, currentSeenby) => {
    try {
      const seenByUsers = currentSeenby ? currentSeenby.split(',').map(id => id.trim()) : [];
      const isAlreadyChecked = seenByUsers.includes(userId.toString());

      // Toggle the user in the seenby list
      const updatedSeenByUsers = isAlreadyChecked
        ? seenByUsers.filter(user => user !== userId.toString()) // Remove user
        : [...seenByUsers, userId.toString()]; // Add user

      const newSeenbyValue = updatedSeenByUsers.join(', '); // Join as a comma-separated string

      const response = await api.post('/pf_alert_check_update', { id, seenby: newSeenbyValue });

      dispatch({
        type: 'UPDATE_SEENBY_STATUS',
        payload: { id, seenby: newSeenbyValue },
      });

    } catch (error) {
      console.error('❌ Failed to update seenby status', error);
    }
  };

  // DAS-2959: one touched alert — clickable, high urgency stands out.
  const renderTouch = (touch, idx) => {
    if (typeof touch === 'string') return <span key={idx}>{idx > 0 ? ', ' : ''}{touch}</span>; // pre-deploy payload shape
    const isHigh = touch.urgency_band === 'HIGH';
    return (
      <span key={touch.alert_id || idx}>
        {idx > 0 ? ', ' : ''}
        <span
          data-testid={isHigh ? 'das2959-touch-high' : 'das2959-touch'}
          onClick={() => openAlertCard(touch.alert_id)}
          style={{
            cursor: 'pointer',
            textDecoration: 'underline',
            ...(isHigh ? { color: '#cf1322', fontWeight: 600 } : {}),
          }}
          title={isHigh ? 'HIGH urgency — click for the alert card' : 'click for the alert card'}
        >
          {isHigh ? '⚡ ' : ''}{touch.ticker} ({touch.alert_type})
        </span>
      </span>
    );
  };

  const columns = [
    {
      title: 'Alert Type',
      dataIndex: 'alert_type',
      key: 'alert_type',
    },
    {
      title: 'Alert Subtype',
      dataIndex: 'alert_subtype',
      key: 'alert_subtype',
    },
    {
      // DAS-2959: days between initiation and last (nightly) update
      title: 'Days active',
      key: 'days_active',
      width: 80,
      render: (_, record) => {
        const d = daysActive(record);
        return d == null ? '' : (
          <Tooltip title="days between initiation and last update">
            <span data-testid="das2959-days">{d}d</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Checked?',
      dataIndex: 'seenby',
      key: 'seenby',
      render: (text, record) => (
        <Checkbox
          checked={isCheckedByUser(record.seenby)}
          onChange={() => handleCheckboxChange(record.ID, record.seenby)}
        />
      ),
    },
    {
      title: 'Alert Text',
      dataIndex: 'pf_alert_text',
      key: 'pf_alert_text',
      render: (text, record) => (
        <Tooltip title={text}>
          <span style={{ whiteSpace: 'pre-wrap' }}>
            {formatAlertText(text)}
            {/* DAS-2775: which of YOUR open ticker alerts this market alert touches */}
            {record.touches && record.touches.length > 0 && (
              <span data-testid="market-alert-touches" style={{ display: 'block', marginTop: 6, color: '#1677ff', fontSize: 12 }}>
                touches your open alerts: {record.touches.map(renderTouch)}
              </span>
            )}
          </span>
        </Tooltip>
      ),
    },
  ];

  // DAS-2959: unseen on top; newest first within each group.
  const sortedAlerts = [...marketAlerts].sort((a, b) => {
    const seenDiff = (isCheckedByUser(a.seenby) ? 1 : 0) - (isCheckedByUser(b.seenby) ? 1 : 0);
    if (seenDiff !== 0) return seenDiff;
    return (b.epoch_initiation || 0) - (a.epoch_initiation || 0);
  });

  return (
    <>
      <Table
        dataSource={sortedAlerts}
        columns={columns}
        rowKey="ID"
        pagination={{ pageSize: 5 }}
      />
      {/* DAS-2959: touched alert opens in place, like the LLM Advice tab (DAS-2897) */}
      <Modal open={!!alertModal} onCancel={() => setAlertModal(null)} footer={null} width={760} destroyOnClose>
        {alertModal && (
          <CompactAlertCard alert={alertModal} alertEffectiveness={alertEffectiveness} onClose={() => setAlertModal(null)} />
        )}
      </Modal>
    </>
  );
};

export default Market_Alerts;
